/**
 * Amortisationsmodell der marktbox.
 *
 * Diese Datei ist die EINZIGE Quelle der Wahrheit für die Rechnung und wird
 * bewusst von Frontend (Angular) und Backend (Node) gemeinsam genutzt.
 * Wer hier eine Zahl ändert, ändert sie überall – im Rechner, in der
 * Lead-Auswertung und im PDF-Versand.
 *
 * Alle Beträge in Euro, netto. Alle Angaben beziehen sich auf EINEN Automaten
 * mit 39 Fächern. Mehrere Automaten lassen sich an einem Standort kombinieren;
 * Versicherung und Mobilfunk fallen dann nur einmal an (siehe Landingpage).
 */

export interface AmortisationConfig {
  /** Kaufpreis netto */
  readonly price: number;
  /** Miete netto pro Monat */
  readonly rent: number;
  /** Mindestlaufzeit der Miete in Monaten */
  readonly rentTermMonths: number;
  /** Dauerleistung in Watt */
  readonly watt: number;
  /** Strompreis in Euro je kWh */
  readonly kwhPrice: number;
  /** Zahlungsgebühr in Euro je Transaktion (Flat, keine Prozente) */
  readonly transactionFee: number;
  /** Versicherung in Euro pro Jahr – fällt je Standort an, nicht je Automat */
  readonly insurancePerYear: number;
  /** Mobilfunk/IoT in Euro pro Monat – fällt je Standort an, nicht je Automat */
  readonly mobilePerMonth: number;
  /** Betriebsstunden pro Monat (24 h × 365 / 12) */
  readonly hoursPerMonth: number;
  /** Wochen pro Monat, für die Arbeitszeitrechnung */
  readonly weeksPerMonth: number;
  /** Steuerliche Abschreibungsdauer laut AfA-Tabelle, in Monaten */
  readonly afaMonths: number;
}

export const AMORTISATION_CONFIG: AmortisationConfig = {
  price: 6800,
  rent: 300,
  rentTermMonths: 12,
  watt: 60,
  kwhPrice: 0.45,
  transactionFee: 0.1,
  insurancePerYear: 200,
  mobilePerMonth: 20,
  hoursPerMonth: 730,
  weeksPerMonth: 4.345,
  afaMonths: 60,
};

/** Warensegment – bestimmt die typische Rohertragsmarge. */
export type Segment = 'direktvermarkter' | 'haendler';

export const SEGMENT_MARGIN: Record<Segment, number> = {
  direktvermarkter: 48,
  haendler: 30,
};

export interface AmortisationInput {
  /** Erwarteter Umsatz pro Monat, netto */
  revenue: number;
  /** Rohertragsmarge in Prozent */
  margin: number;
  /** Durchschnittlicher Einkaufswert pro Kunde */
  basket: number;
  /** Eigene Arbeitszeit mitrechnen? */
  includeLabour: boolean;
  /** Befüllungen pro Woche */
  tripsPerWeek: number;
  /** Minuten je Befüllung */
  minutesPerFill: number;
  /** Fahrtzeit hin und zurück, in Minuten */
  minutesDriving: number;
  /** Stundensatz für die eigene Arbeitszeit */
  hourlyWage: number;
}

/**
 * Startwerte = Mittelwerte aus den ersten 450 marktbox-Automaten
 * an Standorten in Deutschland.
 */
export const PRESET_AVERAGE_CUSTOMER: AmortisationInput = {
  revenue: 2076,
  margin: SEGMENT_MARGIN.direktvermarkter,
  basket: 5.73,
  includeLabour: false,
  tripsPerWeek: 3,
  minutesPerFill: 4,
  minutesDriving: 0,
  hourlyWage: 20,
};

export interface AmortisationResult {
  /** Rohertrag: Umsatz abzüglich Wareneinsatz */
  grossProfit: number;
  powerCost: number;
  insuranceCost: number;
  mobileCost: number;
  /** Transaktionen pro Monat (Umsatz / Bonhöhe) */
  transactions: number;
  transactionCost: number;
  labourHours: number;
  labourCost: number;
  /** Summe aller laufenden Kosten */
  operatingCost: number;
  /** Überschuss pro Monat vor Anschaffung */
  monthlySurplus: number;
  /** Überschuss pro Monat in der Miete */
  monthlySurplusRented: number;
  /** Amortisation in Monaten beim Kauf; null, wenn sie nie eintritt */
  breakevenMonths: number | null;
  /** Liegt die Amortisation innerhalb der steuerlichen Abschreibungsdauer? */
  withinAfa: boolean;
  /**
   * Monat, ab dem der Kauf günstiger ist als die Miete.
   * Umsatzunabhängig: Kaufpreis / Monatsmiete.
   */
  buyBeatsRentFromMonth: number;
}

/**
 * Kern der Rechnung. Bewusst eine reine Funktion ohne Seiteneffekte,
 * damit sie sich testen und serverseitig wiederverwenden lässt.
 */
export function calculateAmortisation(
  input: AmortisationInput,
  config: AmortisationConfig = AMORTISATION_CONFIG,
): AmortisationResult {
  const grossProfit = (input.revenue * input.margin) / 100;

  const powerCost = (config.watt / 1000) * config.hoursPerMonth * config.kwhPrice;
  const insuranceCost = config.insurancePerYear / 12;
  const mobileCost = config.mobilePerMonth;

  const transactions = input.basket > 0 ? input.revenue / input.basket : 0;
  const transactionCost = transactions * config.transactionFee;

  const labourHours = input.includeLabour
    ? (input.tripsPerWeek * (input.minutesPerFill + input.minutesDriving) * config.weeksPerMonth) / 60
    : 0;
  const labourCost = labourHours * input.hourlyWage;

  const operatingCost = powerCost + insuranceCost + mobileCost + transactionCost + labourCost;
  const monthlySurplus = grossProfit - operatingCost;
  const monthlySurplusRented = monthlySurplus - config.rent;

  const breakevenMonths = monthlySurplus > 0 ? config.price / monthlySurplus : null;

  return {
    grossProfit,
    powerCost,
    insuranceCost,
    mobileCost,
    transactions,
    transactionCost,
    labourHours,
    labourCost,
    operatingCost,
    monthlySurplus,
    monthlySurplusRented,
    breakevenMonths,
    withinAfa: breakevenMonths !== null && breakevenMonths <= config.afaMonths,
    buyBeatsRentFromMonth: config.price / config.rent,
  };
}

/**
 * Kumulierter Cashflow über n Monate – Datengrundlage für das Diagramm.
 * Kauf startet im Minus (Anschaffung), Miete bei null.
 */
export function cumulativeCashflow(
  result: AmortisationResult,
  months = 36,
  config: AmortisationConfig = AMORTISATION_CONFIG,
): { buy: number[]; rent: number[] } {
  const buy: number[] = [];
  const rent: number[] = [];
  for (let m = 0; m <= months; m++) {
    buy.push(result.monthlySurplus * m - config.price);
    rent.push(result.monthlySurplusRented * m);
  }
  return { buy, rent };
}
